import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { ROUTE_PATHS, useLanguage, type Language } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t.nav.home, path: ROUTE_PATHS.HOME },
    { label: t.nav.about, path: ROUTE_PATHS.ABOUT },
    { label: t.nav.services, path: ROUTE_PATHS.SERVICES },
    { label: t.nav.advisory, path: ROUTE_PATHS.ADVISORY },
    { label: t.nav.news, path: ROUTE_PATHS.NEWS },
    { label: t.nav.contact, path: ROUTE_PATHS.CONTACT },
  ];

  const languages: { code: Language; name: string; nativeName: string }[] = [
    { code: 'zh-CN', name: 'Simplified Chinese', nativeName: '简体中文' },
    { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文' },
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語' },
    { code: 'ko', name: 'Korean', nativeName: '한국어' },
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 bg-background transition-all duration-200 ${
          scrolled ? 'shadow-md' : ''
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center">
              <img
                src={IMAGES.SHONCHIN_LOGO_TRANSPARENT}
                alt="SHONCHIN MANOR & CO."
                className="h-16 w-auto object-contain"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? 'text-primary' : 'text-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Globe className="w-4 h-4" />
                    <span>{currentLanguage?.nativeName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={language === lang.code ? 'bg-muted' : ''}
                    >
                      <span className="font-medium">{lang.nativeName}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to={ROUTE_PATHS.CONTACT}>
                <Button className="bg-primary text-white rounded-xl px-5 py-2 hover:opacity-90">
                  {t.home.hero.cta}
                </Button>
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background">
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium transition-colors ${
                      isActive ? 'text-primary' : 'text-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-4 border-t border-border">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full gap-2">
                      <Globe className="w-4 h-4" />
                      <span>{currentLanguage?.nativeName}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {languages.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={language === lang.code ? 'bg-muted' : ''}
                      >
                        <span className="font-medium">{lang.nativeName}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-charcoal text-charcoal-foreground">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="mb-6">
                <img
                  src={IMAGES.SHONCHIN_LOGO_TRANSPARENT}
                  alt="SHONCHIN MANOR & CO."
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              </div>
              <p className="text-sm text-charcoal-foreground/80 leading-relaxed mb-4">
                {t.footer.description}
              </p>
              <p className="text-xs text-primary font-medium">{t.footer.slogan}</p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-charcoal-foreground mb-4">
                {t.footer.quickLinks}
              </h3>
              <ul className="space-y-3">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-sm text-charcoal-foreground/70 hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-charcoal-foreground mb-4">
                {t.footer.coreServices}
              </h3>
              <ul className="space-y-3 text-sm text-charcoal-foreground/70">
                <li>{t.services.digitalTwin.title}</li>
                <li>{t.services.verticalStrategy.title}</li>
                <li>{t.services.crossDomain.title}</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-charcoal-foreground mb-4">
                {t.footer.contact}
              </h3>
              <ul className="space-y-3 text-sm text-charcoal-foreground/70">
                <li>
                  <span className="block text-xs text-charcoal-foreground/50 mb-1">
                    {t.footer.dubai}
                  </span>
                  Unit 4206, Mazaya Business Avenue BB2 - JLT, Dubai, UAE
                </li>
                <li>
                  <span className="block text-xs text-charcoal-foreground/50 mb-1">
                    {t.footer.email}
                  </span>
                  contact@shonchin.com
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-charcoal-foreground/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-charcoal-foreground/60">
                {t.footer.copyright}
              </p>
              <div className="flex items-center gap-6 text-xs text-charcoal-foreground/60">
                <Link to={ROUTE_PATHS.PRIVACY} className="hover:text-primary transition-colors">
                  {t.footer.privacy}
                </Link>
                <Link to={ROUTE_PATHS.TERMS} className="hover:text-primary transition-colors">
                  {t.footer.terms}
                </Link>
                <Link to={ROUTE_PATHS.COMPLIANCE} className="hover:text-primary transition-colors">
                  {t.footer.compliance}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}